import { AREngineType, AREngineConfig } from './ar-engine/ar-types';

/**
 * Default AR engine configuration
 * Can be overridden via URL parameters or environment variables
 */
export const DEFAULT_AR_ENGINE: AREngineType = AREngineType.MINDAR;

/**
 * Get AR engine type from URL parameters or default
 * @param urlParams URLSearchParams from window.location.search
 * @returns AR engine type
 */
export function getAREngineFromURL(urlParams: URLSearchParams): AREngineType {
  const engineParam = urlParams.get('engine')?.toLowerCase();

  switch (engineParam) {
    case 'mindar':
    case 'mind-ar':
    case 'mind_ar':
      return AREngineType.MINDAR;
    case 'zappar':
      return AREngineType.ZAPPAR;
    default:
      return DEFAULT_AR_ENGINE;
  }
}

/**
 * Create AR engine configuration based on URL parameters and defaults
 * @param urlParams URLSearchParams from window.location.search
 * @returns Complete AR engine configuration
 */
export function createARConfig(urlParams: URLSearchParams): AREngineConfig {
  const engineType = getAREngineFromURL(urlParams);

  const config: AREngineConfig = {
    engineType,
    engineOptions: {}
  };

  // Engine-specific configuration from URL parameters
  if (engineType === AREngineType.MINDAR) {
    // MindAR-specific parameters
    const filterMinCF = urlParams.get('filterMinCF');
    const filterBeta = urlParams.get('filterBeta');
    const missTolerance = urlParams.get('missTolerance');
    const warmupTolerance = urlParams.get('warmupTolerance');
    const maxTrack = urlParams.get('maxTrack');
    const uiLoading = urlParams.get('uiLoading');
    const uiScanning = urlParams.get('uiScanning');
    const parseBooleanParam = (value: string | null, defaultValue: boolean): boolean => {
      if (value === null) return defaultValue;
      return value === 'true';
    };

    config.engineOptions!.mindar = {
      // OneEuroFilter parameters for smooth, stable tracking
      // Higher filterMinCF = more smoothing (less jitter, but slight lag)
      // Lower filterBeta = less responsive to rapid changes (reduces jitter from camera shake)
      //
      // BALANCED SETTINGS for reduced jitter:
      // - filterMinCF: 0.1 provides good smoothing while still being responsive
      // - filterBeta: 10 reduces reaction to small movements (camera shake)
      //
      // For testing via URL parameters:
      // - More smoothing: ?filterMinCF=0.2&filterBeta=5
      // - Less smoothing: ?filterMinCF=0.05&filterBeta=50
      filterMinCF: filterMinCF ? parseFloat(filterMinCF) : 0.1,    // Increased from 0.001 for smoother tracking
      filterBeta: filterBeta ? parseFloat(filterBeta) : 10,        // Decreased from 1000 to reduce jitter

      // Tolerance values prevent flickering by requiring multiple consecutive frames
      missTolerance: missTolerance ? parseInt(missTolerance) : 10,        // Frames before losing tracking
      warmupTolerance: warmupTolerance ? parseInt(warmupTolerance) : 5,   // Frames for initial detection
      maxTrack: maxTrack ? parseInt(maxTrack) : 6,  // Support up to 6 simultaneous targets
      uiLoading: parseBooleanParam(uiLoading, false),  // Keep loading overlay disabled
      uiScanning: parseBooleanParam(uiScanning, true)  // Enable scanning overlay for visual feedback
    };
  } else if (engineType === AREngineType.ZAPPAR) {
    // Zappar-specific parameters
    const useLoadingManager = urlParams.get('useLoadingManager');

    config.engineOptions!.zappar = {
      useLoadingManager: useLoadingManager === 'true'
    };
  }

  return config;
}

/**
 * Asset path helper - gets the correct target file based on AR engine
 * @param baseName Base name of the asset (without extension)
 * @param engineType AR engine type
 * @returns Full path to the target file
 */
export function getTargetFilePath(baseName: string, engineType: AREngineType): string {
  if (engineType === AREngineType.MINDAR) {
    // MindAR uses card1.mind
    return new URL('./assets/card1.mind', import.meta.url).href;
  }

  // Zappar uses .zpt files
  return new URL(`./assets/${baseName}.zpt`, import.meta.url).href;
}

/**
 * Log AR configuration to console
 */
export function logARConfig(config: AREngineConfig, targetFile?: string): void {
  console.log(`
🎯 AR Engine Configuration:

   Engine: ${config.engineType.toUpperCase()}
   ${targetFile ? `Target File: ${targetFile.split('/').pop()}` : ''}
   ${config.engineType === AREngineType.MINDAR ? `
   MindAR Options:
   - Filter Min CF: ${config.engineOptions?.mindar?.filterMinCF ?? 0.1} (higher = smoother)
   - Filter Beta: ${config.engineOptions?.mindar?.filterBeta ?? 10} (lower = less jittery)
   - Miss Tolerance: ${config.engineOptions?.mindar?.missTolerance ?? 10} frames
   - Warmup Tolerance: ${config.engineOptions?.mindar?.warmupTolerance ?? 5} frames
   - Max Track: ${config.engineOptions?.mindar?.maxTrack ?? 6} targets
   - UI Loading: ${config.engineOptions?.mindar?.uiLoading !== false}
   - UI Scanning: ${config.engineOptions?.mindar?.uiScanning !== false}
   ` : ''}
   ${config.engineType === AREngineType.ZAPPAR ? `
   Zappar Options:
   - Loading Manager: ${config.engineOptions?.zappar?.useLoadingManager === true}
   ` : ''}

   Switch engine via URL:
   (Default) MindAR: ?engine=mindar
   Legacy Zappar: ?engine=zappar

   Adjust tracking smoothness (MindAR):
   - Less jitter: ?filterMinCF=0.2&filterBeta=5
   - Default: filterMinCF=0.1, filterBeta=10
   - More responsive: ?filterMinCF=0.05&filterBeta=50
  `);
}
